import { translate } from '@avensia/excite';
import { styled } from '@glitz/react';
import Price from 'nordicnest/product/Price';
import * as React from 'react';
import Link from 'webapp/components/Link';
import { Button } from 'webapp/components/button';
import { FieldSize } from 'webapp/components/field-size';
import { CheckboxField } from 'webapp/components/form/checkbox-field';
import { SquareImage } from 'webapp/components/image';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { Variant } from 'webapp/product/price-variant';
import { linkTitle } from 'webapp/product/product-seo-info';
import { a11yGrey, black, greenText } from 'webapp/shared/sharedTheme';
import { Theme } from 'webapp/theme';
import { applyPercentagePrice } from './helpers';
import { useFormatPrice } from '@avensia/excite-ui';

type Props = {
  accessories: NordicNest.Api.ProductAccessoryInfoApiModel[] | undefined;
  updateAccessoriesAdded: (
    accessories: (NordicNest.Api.PartialProductApiModel & { quantity: number })[],
    remove?: boolean,
  ) => void;
  quantity: number;
};

export const ProductAccessoryCards = function ProductAccessories({
  accessories,
  updateAccessoriesAdded,
  quantity,
}: Props) {
  const [showAll, setShowAll] = React.useState<boolean>(false);
  const itemsToShow = 4;
  const formatPrice = useFormatPrice();
  const validAccessories = accessories?.filter(a => a.quantity && a.quantity > 0);
  if (!validAccessories || validAccessories.length === 0) return null;

  return (
    <AccessoriesWrapper>
      {validAccessories
        .slice(0, showAll ? validAccessories.length : itemsToShow)
        .map(({ accessoryProduct: product, discountPercent, quantity: accessoryQuantity }) => (
          <AccessoryContainer key={product.displayName}>
            <Link title={linkTitle(product)} to={product.url} linkIdentifier={product.ticket}>
              <SquareImage image={product.images[0]} preset={'thumb'} imageCss={paddingDecorator} />
            </Link>
            <AccessoryContent title={linkTitle(product)} to={product.url} linkIdentifier={product.ticket}>
              <BrandAndName css={!product.availableInCountry && paddingRightDecorator}>
                <Brand>{product.brand}</Brand>
                <Name>{product.displayName}</Name>
              </BrandAndName>
            </AccessoryContent>
            <PriceWrapper>
              {product.availableInCountry && (
                <>
                  {discountPercent > 0 && (
                    <DiscountPercentage>
                      {translate(t => t.product.accessoriesDiscountText, { discount: discountPercent })}
                    </DiscountPercentage>
                  )}
                  <Wrapper>
                    <PriceNew
                      prefix={
                        accessoryQuantity > 1
                          ? `${quantity * accessoryQuantity} ${translate(t => t.productPage.productOneUnit)} x`
                          : ''
                      }
                      variant={Variant.Compact}
                      price={applyPercentagePrice(product.price, discountPercent)}
                    />
                    {discountPercent > 0 && <CurrentPrice>{formatPrice(product.price.current.inclVat)}</CurrentPrice>}
                  </Wrapper>
                </>
              )}
            </PriceWrapper>
            {product.availableInCountry && (
              <CheckboxWrapper>
                <Checkbox
                  onChange={e =>
                    e.currentTarget.checked
                      ? updateAccessoriesAdded([{ ...product, quantity: accessoryQuantity }])
                      : updateAccessoriesAdded([{ ...product, quantity: accessoryQuantity }], true)
                  }
                />
              </CheckboxWrapper>
            )}
          </AccessoryContainer>
        ))}
      {validAccessories.length > itemsToShow && (
        <ShowMoreButton
          onClick={() => setShowAll(!showAll)}
          as={Theme.WishlistButton}
          size={FieldSize.XSmall}
          disableRippleEffect
        >
          {showAll ? translate(t => t.product.showLessAccessories) : translate(t => t.product.showMoreAccessories)}
        </ShowMoreButton>
      )}
    </AccessoriesWrapper>
  );
};

const AccessoriesWrapper = styled.div({
  marginBottom: t => t.sharedSpacing.xl,
});

const Checkbox = styled(CheckboxField, {
  fontSize: t => t.sharedFontSize.xxxs,
  margin: { x: t => t.sharedSpacing.m },
});

const AccessoryContainer = styled.div({
  backgroundColor: 'white',
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: '64px minmax(0, 1fr) auto auto',
  border: { xy: { width: 'thin', color: t => t.borderStrongColor, style: 'solid' }, top: { width: 0 } },
  ':first-child': {
    border: { top: { width: 'thin' } },
  },
});

const AccessoryContent = styled(Link, {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minWidth: 0,
  padding: { left: t => t.sharedSpacing.xs, right: t => t.sharedSpacing.m },
});

const BrandAndName = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const Brand = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.sharedFontSize.xxs,
  fontWeight: t => t.fontWeight?.normal,
  textTransform: 'uppercase',
  minHeight: '1.5em',
  color: black,
});

const Name = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.sharedFontSize.xxs,
  color: a11yGrey,
});

const PriceWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
});

const DiscountPercentage = styled.div({
  fontSize: t => t.sharedFontSize.xxs,
  fontWeight: t => t.fontWeight?.bold,
  color: greenText,
});

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: t => t.sharedSpacing.xs,
});

const PriceNew = styled(Price, {
  flexDirection: 'column',
  fontSize: t => t.sharedFontSize.xxs,
  fontWeight: t => t.fontWeight?.bold,
  justifyContent: 'center',
  alignItems: 'end',
  height: '100%',
  color: black,
});

const CurrentPrice = styled.span({
  textDecoration: 'line-through',
  color: a11yGrey,
  fontSize: t => t.sharedFontSize.xxs,
  fontWeight: t => t.fontWeight?.light,
});

const CheckboxWrapper = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const paddingDecorator = styled({
  padding: { xy: t => t.sharedSpacing.s },
});

const paddingRightDecorator = styled({
  paddingRight: t => t.sharedSpacing.xs,
});

const ShowMoreButton = styled(Button, {
  margin: { top: t => t.sharedSpacing.xs },
  width: '100%',
  fontSize: t => t.sharedFontSize.xs,
  height: 41,
  fontWeight: 400,
});
